import React, { useState, useMemo } from "react";
import { Table, DatePicker, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetInventoryReportReturnedItems } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const ReturnedItemsReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["returned-items-report", startDateStr, endDateStr],
    queryFn: () =>
      GetInventoryReportReturnedItems({
        start_date: startDateStr,
        end_date: endDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const good = reportData
      .filter((x: any) => x.return_type_name === "Good")
      .reduce((sum: number, item: any) => sum + (item.quantity || 0), 0);
    const damaged = reportData
      .filter((x: any) => x.return_type_name === "Damaged")
      .reduce((sum: number, item: any) => sum + (item.quantity || 0), 0);
    const expired = reportData
      .filter((x: any) => x.return_type_name === "Expire")
      .reduce((sum: number, item: any) => sum + (item.quantity || 0), 0);
    const refund = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_refund || 0),
      0,
    );
    return [
      {
        title: "Good Qty (To Stock)",
        value: `${good} Units`,
        colorClass: "text-emerald-600",
      },
      {
        title: "Damaged / Expired Qty",
        value: `${damaged} Damaged / ${expired} Expired`,
        colorClass: "text-rose-500",
      },
      {
        title: "Total Refunded Outflow",
        value: formatLKR(refund),
        colorClass: "text-rose-600 font-extrabold",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Return Date",
      dataIndex: "return_date",
      key: "return_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Return No",
      dataIndex: "return_no",
      key: "return_no",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-800">{v}</span>
      ),
    },
    {
      title: "Product Title",
      dataIndex: "product_title",
      key: "product_title",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-800">{v}</span>
          <span className="text-[10px] text-gray-400">
            SKU: {record.product_sku}
          </span>
        </div>
      ),
    },
    {
      title: "Returned Condition",
      dataIndex: "return_type_name",
      key: "return_type_name",
      render: (v: string) => {
        let color = "success";
        if (v === "Damaged") color = "error";
        if (v === "Expire") color = "warning";
        return (
          <Tag
            color={color}
            className="rounded-full font-bold text-[10px] uppercase"
          >
            {v}
          </Tag>
        );
      },
    },
    {
      title: "Returned Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      render: (v: number) => (
        <span className="text-xs font-semibold text-gray-700">{v}</span>
      ),
    },
    {
      title: "Refund Rate",
      dataIndex: "refund_price",
      key: "refund_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-gray-500 font-medium">
          LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Total Refunded",
      dataIndex: "total_refund",
      key: "total_refund",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-rose-600">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Return Item Report"
      description="Analyze items returned by customers categorized by quality condition (Good / Damaged / Expired)."
      icon={<RotateCcw className="w-6 h-6 text-amber-600" />}
      backPath="/system/inventory-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) =>
            record.id || record.return_no || Math.random().toString()
          }
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
